/**
 * Hold a chase binder or a set row: share, or stop chasing it.
 *
 * The collection's BinderActionSheet cannot serve here even though it looks
 * right, because its delete step asks whether the cards should leave the
 * collection too. A wish surface holds nothing anyone owns, so that question
 * has no meaning and asking it would suggest removing a master set could cost
 * someone a card. Removing one removes an intention.
 */

import { useEffect, useState } from 'react';
import { Share2, Trash2, X } from 'lucide-react';
import BottomSheet from '../BottomSheet';
import { useTheme } from '../../context/ThemeContext';
import haptics from '../../utils/haptics';

export default function WishSurfaceHoldSheet({
  isOpen,
  surface,
  kindLabel = 'chase binder',
  onClose,
  onShare,
  onRemove,
}) {
  const { theme } = useTheme();
  const [step, setStep] = useState('menu');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => { if (isOpen) { setStep('menu'); setError(''); setBusy(false); } }, [isOpen]);

  const close = () => { if (!busy) onClose?.(); };

  const run = async (fn, failure) => {
    setBusy(true);
    setError('');
    try {
      await fn();
      onClose?.();
    } catch (e) {
      console.warn('[wish surface] action failed', e?.message);
      setError(failure);
      setBusy(false);
      setStep('menu');
    }
  };

  return (
    <BottomSheet
      open={!!isOpen && !!surface}
      onClose={close}
      label="Chase list actions"
      overlayStyle={{ ...styles.overlay, backgroundColor: theme.overlay }}
      sheetStyle={{ ...styles.sheet, backgroundColor: theme.bgSheet }}
    >
      <div style={styles.handle} />
      <div style={styles.header}>
        <div style={styles.titleBlock}>
          <h2 style={{ ...styles.title, color: theme.text }}>{surface?.name}</h2>
          <span style={{ ...styles.meta, color: theme.textMuted }}>{kindLabel}</span>
        </div>
        <button
          style={{ ...styles.closeBtn, backgroundColor: theme.bgCard, borderColor: theme.border, color: theme.textSecondary }}
          onClick={close}
          aria-label="Close"
        >
          <X size={16} />
        </button>
      </div>

      {error && <p style={{ ...styles.error, color: theme.error }}>{error}</p>}

      {step === 'menu' && (
        <>
          <button
            style={{ ...styles.row, backgroundColor: theme.bgCard, borderColor: theme.border, color: theme.text }}
            onClick={() => { haptics.tap(); run(() => onShare(surface), 'That share did not go through.'); }}
            disabled={busy}
          >
            <Share2 size={18} color={theme.accent} />
            <span style={styles.rowLabel}>Share</span>
          </button>
          <button
            style={{ ...styles.row, backgroundColor: theme.bgCard, borderColor: theme.border, color: theme.error }}
            onClick={() => { haptics.tap(); setStep('confirm'); }}
            disabled={busy}
          >
            <Trash2 size={18} color={theme.error} />
            <span style={styles.rowLabel}>Stop chasing this</span>
          </button>
        </>
      )}

      {step === 'confirm' && (
        <>
          <p style={{ ...styles.confirm, color: theme.text }}>
            Take "{surface?.name}" off your chase list? Nothing you own is
            touched - you just are not chasing it right now.
          </p>
          <button
            style={{ ...styles.primary, backgroundColor: theme.error }}
            onClick={() => run(() => onRemove(surface), 'Could not remove that. Try again.')}
            disabled={busy}
          >
            <Trash2 size={16} /> {busy ? 'Removing...' : 'Remove it'}
          </button>
          <button
            style={{ ...styles.row, backgroundColor: theme.bgCard, borderColor: theme.border, color: theme.text }}
            onClick={() => setStep('menu')}
            disabled={busy}
          >
            <span style={styles.rowLabel}>Cancel</span>
          </button>
        </>
      )}
    </BottomSheet>
  );
}

const styles = {
  overlay: { position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, zIndex: 10002 },
  sheet: {
    position: 'fixed',
    bottom: 0,
    left: 0,
    right: 0,
    borderRadius: '20px 20px 0 0',
    padding: '12px 20px calc(24px + var(--safe-area-bottom, 0px))',
    zIndex: 10003,
    display: 'flex',
    flexDirection: 'column',
    gap: '8px',
    boxShadow: '0 -4px 20px rgba(0, 0, 0, 0.5)',
  },
  handle: { width: '36px', height: '4px', backgroundColor: '#3a3a3a', borderRadius: '2px', margin: '0 auto 12px' },
  header: { display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: '12px', marginBottom: '4px' },
  titleBlock: { display: 'flex', flexDirection: 'column', gap: '2px', minWidth: 0 },
  title: { fontSize: '17px', fontWeight: '600', margin: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' },
  meta: { fontSize: '12px' },
  closeBtn: {
    width: '30px', height: '30px', borderRadius: '8px', border: '1px solid', display: 'flex',
    alignItems: 'center', justifyContent: 'center', cursor: 'pointer', outline: 'none', flexShrink: 0,
    WebkitTapHighlightColor: 'transparent',
  },
  error: { fontSize: '13px', margin: '0 0 2px', fontWeight: '600' },
  confirm: { fontSize: '14px', margin: '0 0 4px', lineHeight: 1.45 },
  row: {
    display: 'flex', alignItems: 'center', gap: '12px', minHeight: '52px', padding: '0 14px',
    borderRadius: '12px', border: '1px solid', cursor: 'pointer', textAlign: 'left',
    outline: 'none', WebkitTapHighlightColor: 'transparent',
  },
  rowLabel: { fontSize: '15px', fontWeight: '600' },
  primary: {
    display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px', minHeight: '48px',
    borderRadius: '12px', border: 'none', color: '#fff', fontSize: '15px', fontWeight: '700',
    cursor: 'pointer', outline: 'none', WebkitTapHighlightColor: 'transparent',
  },
};
